import {
  AfterViewChecked,
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core'
import { FormGroup } from '@angular/forms'
import { SwiperContainer } from 'swiper/element'
import { register } from 'swiper/element/bundle'
import { A11y, Mousewheel, Navigation } from 'swiper/modules'
import { IsImagePipe } from '../../pipes/is-image.pipe'
import { FileExtensionPipe } from '../../pipes/file-extension.pipe'
import { AppendFileDomainPipe } from '../../pipes/append-file-domain.pipe'
import { Gallery } from '../../types/upload.type'

@Component({
  selector: 'gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [IsImagePipe, FileExtensionPipe, AppendFileDomainPipe]
})
export class GalleryComponent implements AfterViewInit, AfterViewChecked {
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.onClose()
    }
  }

  // Images array to show in the carousel
  @Input() set images(imagesArray: Gallery[] | FormGroup[]) {
    if (imagesArray[0] instanceof FormGroup) {
      this.imagesArray = imagesArray
        .map((file) => {
          return {
            src: (file as FormGroup).value.cropped_path || (file as FormGroup).value.path,
            name: (file as FormGroup).value.name,
            cropperId: ''
          }
        })
        .filter((img) => img.src !== '')
    } else {
      imagesArray
        .filter((img) => (img as Gallery).src !== '')
        .forEach((img) => {
          const fileExtension = (img as Gallery)?.src?.split('.')?.pop()?.toLowerCase() || ''
          this.imagesArray.push({
            src: ['jpg', 'jpeg', 'png', 'svg'].includes(fileExtension) ? (img as Gallery).src : '',
            name: (img as Gallery).name,
            cropperId: (img as Gallery).cropperId
          })
        })
    }
  }
  @Input() set selectedIndex(index: number | undefined) {
    this.currentSelectedIndex = index || 0
  }
  @Input() useCropper: boolean = true
  // An event emitted to close the carousel
  @Output() onGalleryClose = new EventEmitter()
  // An event emitted to open cropper to edit image
  @Output() openCropperModal = new EventEmitter()
  // The current image name that is showed in the carousel footer
  currentImageName!: string
  currentImageIndex!: number
  currentSelectedIndex!: number
  imagesArray: Gallery[] = []
  activeSlideIndex!: number
  @ViewChild('swiperElement') swiperElement!: ElementRef<SwiperContainer>
  constructor(private _Cdr: ChangeDetectorRef) {
    register()
  }

  config: any = {
    modules: [Mousewheel, Navigation, A11y],
    autoHeight: true,
    spaceBetween: 20,
    slidesPerView: 1,
    centeredSlides: true,
    allowSlideNext: true,
    allowSlidePrev: true,
    followFinger: true,
    grabCursor: true,
    updateOnWindowResize: true,
    speed: 500,
    simulateTouch: true,
    keyboard: true,
    navigation: {
      enabled: true,
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    },
    breakpoints: {
      992: {
        navigation: {
          enabled: true,
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        }
      },
      767.98: {
        navigation: {
          enabled: false
        }
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this._initializeSwiper())
  }

  /**
   * Initialize swiper
   * @returns void
   */
  private _initializeSwiper(): void {
    const element = this.swiperElement?.nativeElement
    if (!element) return
    Object.assign(element, { ...this.config, initialSlide: this.currentSelectedIndex })
    element.initialize()
    this.swiperElement.nativeElement.swiper?.keyboard?.enable()
    this.swiperElement.nativeElement.swiper?.navigation?.update()

    // Force slide to the correct index to avoid flash
    this.swiperElement.nativeElement.swiper?.slideTo(this.currentSelectedIndex, 0)

    this.activeSlideIndex = this.currentSelectedIndex
    this.swiperElement.nativeElement?.swiper?.on('slideChange', () => {
      this.activeSlideIndex = this.swiperElement.nativeElement?.swiper?.activeIndex
      this._Cdr.detectChanges()
    })
    this.customizeStyles()
  }

  ngAfterViewChecked(): void {
    this._Cdr.detectChanges()
  }

  // Method used to customize swiper navigation buttons
  private customizeStyles() {
    const shadowRoot = this.swiperElement.nativeElement.shadowRoot
    const buttonSelectors = ['.swiper-button-prev', '.swiper-button-next']

    const svgPaths = shadowRoot?.querySelectorAll('svg > path')
    const svgs = shadowRoot?.querySelectorAll('svg')
    if (svgPaths) {
      svgPaths.forEach((path) => ((path as SVGPathElement).style.fill = 'white'))
    }
    if (svgs) {
      svgs.forEach((svg) => ((svg as SVGElement).style.width = '10px'))
    }

    buttonSelectors.forEach((selector) => {
      const button = shadowRoot?.querySelector(selector) as HTMLElement
      if (button) {
        button.style.width = '40px'
        button.style.height = '40px'
        button.style.borderRadius = '50%'
        button.style.backgroundColor = '#374151'

        if (selector === '.swiper-button-prev') {
          button.style.left = '50px'
        } else if (selector === '.swiper-button-next') {
          button.style.right = '50px'
        }
      }
    })
  }
  /**
   * Method used to emit an event to close the gallery
   * @returns void
   */
  onClose(): void {
    this.onGalleryClose.emit()
    this.swiperElement?.nativeElement?.swiper?.destroy(true)
  }

  onEdit(index: number) {
    this.openCropperModal.emit(this.imagesArray[this.activeSlideIndex].cropperId || index)
    this.onClose()
  }
}
